// A page written together (docs/live-design.md §3.3–3.7): joins the page's live session, sends this browser's steps and
// takes in everyone else's, sends snapshots when typing pauses (the server puts the page together from its parts and
// saves it), and shows where the others are. The text is in parts (§3.4): the page's own, every editor's, and one per
// restricted block, opened (as an editor in its box) only by those it's for; everyone else sees a locked box. The Visual
// editor does the document work (collab.ts).

import { connection } from "../components/p9-live-page.js";
import { principalsInput } from "./toolbar-ui.js";
import { liveRows, type RowsPart } from "./live-rows.js";
import { NOT_REVEALED, writeRows, type Placed } from "./rows.js";
import type { Cursor, LiveStart, PartsHost } from "./collab.js";
import type { VisualEditor } from "./visual.js";

type PartState = { key: string; version: number; baseVersion: number; markdown: string | null; doc: string | null; steps: string[]; clientIds: string[] };
type VisiblePart = { key: string; parent: string | null; to: string; rows: boolean };
type Opened = { live: boolean; reason: string | null; sessionId: string; shared: PartState | null; parts: VisiblePart[]; title: string; problems: string | null };
type Hub = Awaited<ReturnType<typeof connection>>;

const QUIET_MS = 2000;
const LONGEST_MS = 15000;
const COLORS = ["#b5651d", "#2e7d6b", "#7b4fa0", "#2f6fae", "#a33a5b", "#5f7d1f", "#9a6a00", "#3d5a80"];

/** How the page around the editor shows the session, and makes the editors of restricted parts. */
export type LiveHost = {
  status: (text: string, problem?: boolean) => void;
  /** The page's title, summary and font as the form has them (sent with the page's snapshots). */
  fields: () => { title: string; summary: string; font: string };
  setTitle: (title: string) => void;
  /** The session can't go on here (e.g. the browser was away too long): keep the text and start again. */
  restart: (reason: string, markdown: string | null) => void;
  /** A version was saved: the form's base revision follows (for a save in Markdown mode afterwards). */
  saved: (revisionId: string | null) => void;
  /** An editor for a restricted part's text, inside its box. */
  partEditor: (mount: HTMLElement, markdown: string, start: LiveStart, onChange: () => void) => VisualEditor;
  lookupUrl: string;
  me?: string;
};

/** One live part being written here: the page's own ("") or a restricted block's. */
class Link {
  editor: VisualEditor | null = null;
  /** The box's content for a restricted part: its editor and controls, moved along when its placeholder is redrawn. */
  element: HTMLElement | null = null;
  pushing = false;
  catchingUp: Promise<void> | null = null;
  quiet: number | undefined;
  longest: number | undefined;
  snapshotted = -1;
  cursorTimer: number | undefined;
  cursors = new Map<string, Cursor & { userId: string }>();
  closed = false;

  constructor(private owner: LiveEditing, readonly key: string) {}

  get live() {
    return this.closed ? undefined : this.editor?.live;
  }

  get unsaved(): boolean {
    const live = this.live;
    return Boolean(live && (live.sendable() || live.version() > this.snapshotted));
  }

  changed(): void {
    window.clearTimeout(this.quiet);
    this.quiet = window.setTimeout(() => void this.snapshot(), QUIET_MS);
    this.longest ??= window.setTimeout(() => void this.snapshot(), LONGEST_MS);
  }

  schedulePush(): void {
    if (this.pushing || this.closed) return;
    this.pushing = true;
    window.setTimeout(() => void this.push(), 30);
  }

  async push(): Promise<void> {
    try {
      const sendable = this.live?.sendable();
      if (!sendable) return;
      const ok = (await this.owner.hub.invoke("Push", this.owner.page, this.owner.sessionId, this.key, sendable.version, sendable.steps, sendable.clientID)) as boolean;
      // Refused: someone else's steps came first; they're on their way (or fetched now), then ours go again.
      if (!ok) await this.catchup();
    } catch {
      // The connection dropped: reconnecting sends them.
    } finally {
      this.pushing = false;
    }
    if (this.live?.sendable()) window.setTimeout(() => this.schedulePush(), 50);
  }

  async received(from: number, steps: string[], clientIds: string[]): Promise<void> {
    const live = this.live;
    if (!live) return;
    const version = live.version();
    if (from > version) return void (await this.catchup());
    if (from + steps.length <= version) return;
    const skip = version - from;
    live.receive(steps.slice(skip), clientIds.slice(skip));
  }

  async catchup(): Promise<void> {
    const live = this.live;
    if (!live) return;
    // One at a time, and only what hasn't arrived by now: steps sent to everyone may come in while this one is asked.
    if (this.catchingUp) return this.catchingUp;
    this.catchingUp = (async () => {
      const asked = live.version();
      const missed = (await this.owner.hub.invoke("Catchup", this.owner.page, this.owner.sessionId, this.key, asked)) as { steps: string[]; clientIds: string[] } | null;
      if (missed === null) return this.owner.restart("You were away from the live text for too long to catch up.");
      const skip = live.version() - asked;
      if (skip < missed.steps.length) live.receive(missed.steps.slice(skip), missed.clientIds.slice(skip));
    })();
    try {
      await this.catchingUp;
    } finally {
      this.catchingUp = null;
    }
  }

  async snapshot(): Promise<void> {
    window.clearTimeout(this.quiet);
    window.clearTimeout(this.longest);
    this.longest = undefined;
    const live = this.live;
    if (!live) return;
    const snapshot = live.snapshot();
    // Own steps still on their way: once they're through.
    if (!snapshot) return void (this.quiet = window.setTimeout(() => void this.snapshot(), 300));
    if (snapshot.version <= this.snapshotted) return;
    const { title, summary, font } = this.key === "" ? this.owner.host.fields() : { title: "", summary: "", font: "" };
    try {
      await this.owner.hub.invoke("Snapshot", this.owner.page, this.owner.sessionId, this.key, snapshot.version, snapshot.markdown, snapshot.doc, title,
        summary || null, font || null);
      this.snapshotted = Math.max(this.snapshotted, snapshot.version);
    } catch {
      // Sent again with the next change.
    }
  }

  cursor(anchor: number, head: number): void {
    window.clearTimeout(this.cursorTimer);
    this.cursorTimer = window.setTimeout(() => {
      const live = this.live;
      if (live) void this.owner.hub.invoke("Cursor", this.owner.page, this.owner.sessionId, this.key, live.version(), anchor, head).catch(() => {});
    }, 120);
  }

  showCursor(m: { version: number; anchor: number; head: number; connection: string; userId: string; name: string }): void {
    const live = this.live;
    // A cursor from another version would point at the wrong place: the next one will do.
    if (!live || m.version !== live.version()) return;
    this.cursors.set(m.connection, { key: m.connection, userId: m.userId, name: m.name, color: color(m.userId), anchor: m.anchor, head: m.head });
    live.cursors([...this.cursors.values()]);
  }

  dropCursors(editing: Array<{ id: string }>): void {
    for (const [key, c] of this.cursors) if (!editing.some((e) => e.id === c.userId)) this.cursors.delete(key);
    this.live?.cursors([...this.cursors.values()]);
  }

  // A change from outside the live text (an AI suggestion, the tracker, Markdown mode…) merged by the server: put in
  // here, as a step of this browser's own, moved past the steps since the snapshot it was merged into.
  async outside(m: { requestId: string; version: number; doc: string | null; markdown: string | null; to: string }): Promise<void> {
    let applied = false;
    try {
      const live = this.live;
      if (live) {
        if (live.version() < m.version) await this.catchup();
        const since = (await this.owner.hub.invoke("Catchup", this.owner.page, this.owner.sessionId, this.key, m.version)) as { steps: string[] } | null;
        if (since && live.version() >= m.version) {
          applied = live.applyOutside(m.doc ? JSON.parse(m.doc) : null, m.markdown, m.to, since.steps.slice(0, live.version() - m.version));
          if (applied) {
            // Sent before saying so: the server then knows any snapshot without this change is an older one.
            const until = Date.now() + 3000;
            while (Date.now() < until && this.live?.sendable()) {
              this.schedulePush();
              await sleep(60);
            }
            this.changed();
          }
        }
      }
    } finally {
      await this.owner.hub.invoke("OutsideDone", this.owner.page, m.requestId, applied).catch(() => {});
    }
  }

  close(): void {
    this.closed = true;
    window.clearTimeout(this.quiet);
    window.clearTimeout(this.longest);
    if (this.key !== "") this.editor?.destroy();
  }
}

export class LiveEditing {
  hub!: Hub;
  #opened!: Opened;
  #links = new Map<string, Link>();
  /** The restricted parts this person may open, by key. */
  #visible = new Map<string, VisiblePart>();
  /** The boxes (placeholders) drawn for restricted parts, to redraw when what may be opened changes. */
  #boxes = new Map<string, Set<HTMLElement>>();
  /** The text of the restricted rows this person may see. */
  #rows = new Map<string, string>();
  #handlers: Array<[string, (...args: never[]) => void]> = [];
  #closed = false;
  readonly clientID = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

  constructor(readonly page: string, readonly host: LiveHost) {}

  get sessionId(): string {
    return this.#opened.sessionId;
  }

  /** Joins the session; null when the page isn't live here (the reason is shown) or the connection isn't there. */
  async open(): Promise<{ start: LiveStart; markdown: string | null; title: string } | null> {
    try {
      this.hub = await connection();
      await this.hub.invoke("Join", this.page, true);
      this.#opened = (await this.hub.invoke("OpenDoc", this.page)) as Opened;
    } catch {
      return null;
    }
    if (!this.#opened.live || !this.#opened.shared) {
      if (this.#opened.reason) this.host.status(this.#opened.reason);
      return null;
    }
    this.#on("steps", (m: { pageId: string; sessionId: string; key: string; from: number; steps: string[]; clientIds: string[] }) => {
      if (this.#mine(m)) void this.#links.get(m.key)?.received(m.from, m.steps, m.clientIds);
    });
    this.#on("saved", (m: { pageId: string; revisionId: string | null }) => {
      if (m.pageId !== this.page) return;
      this.host.saved(m.revisionId);
      this.host.status(`Saved ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`);
    });
    this.#on("problems", (m: { pageId: string; problems: string }) => m.pageId === this.page && this.host.status(`Not saved to the page yet: ${m.problems}`, true));
    this.#on("title", (m: { pageId: string; title: string }) => m.pageId === this.page && this.host.setTitle(m.title));
    this.#on("ended", (m: { pageId: string; reason: string }) => m.pageId === this.page && this.restart(m.reason));
    this.#on("cursor", (m: { pageId: string; sessionId: string; key: string; version: number; anchor: number; head: number; connection: string; userId: string; name: string }) => {
      if (this.#mine(m)) this.#links.get(m.key)?.showCursor(m);
    });
    this.#on("presence", (p: { pageId: string; editing: Array<{ id: string }> }) => {
      if (p.pageId === this.page) for (const link of this.#links.values()) link.dropCursors(p.editing);
    });
    this.#on("outside", (m: { pageId: string; sessionId: string; key: string; requestId: string; version: number; doc: string | null; markdown: string | null; to: string }) => {
      if (this.#mine(m)) void this.#links.get(m.key)?.outside(m);
      else if (m.pageId === this.page) void this.hub.invoke("OutsideDone", this.page, m.requestId, false).catch(() => {});
    });
    this.#on("outsideMerged", (m: { pageId: string; summary: string | null }) =>
      m.pageId === this.page && this.host.status(`A change from outside the editor was merged in${m.summary ? `: ${m.summary}` : ""}.`));
    // Parts were added (a block restricted just now) or an audience changed: what this person may open, again.
    this.#on("parts", (m: { pageId: string }) => m.pageId === this.page && void this.#refreshParts());
    this.#on("partHidden", (m: { pageId: string; key: string }) => m.pageId === this.page && this.#hide(m.key));
    this.#on("rowsChanged", (m: { pageId: string; key: string; text: string }) => {
      if (m.pageId !== this.page || !this.#rows.has(m.key)) return;
      this.#rows.set(m.key, m.text);
      liveRows.changed();
    });
    this.hub.onreconnected(() => void this.#reconnected());

    for (const part of this.#opened.parts) this.#visible.set(part.key, part);
    await this.#openRows();
    liveRows.set(this.#rowsApi());
    if (this.#opened.problems) this.host.status(`Not saved to the page yet: ${this.#opened.problems}`, true);
    else this.host.status("Live: others who open this page write with you.");
    const shared = this.#opened.shared;
    const main = new Link(this, "");
    this.#links.set("", main);
    return { start: this.#start(main, shared), markdown: shared.markdown, title: this.#opened.title };
  }

  #mine(m: { pageId: string; sessionId: string }): boolean {
    return m.pageId === this.page && m.sessionId === this.#opened.sessionId;
  }

  // Where a part's editor starts, and how it reports.
  #start(link: Link, state: PartState): LiveStart {
    return {
      version: state.version, doc: state.doc ? JSON.parse(state.doc) : null, steps: state.steps, clientIds: state.clientIds, clientID: this.clientID,
      onSendable: () => link.schedulePush(), onCursor: (a, h) => link.cursor(a, h), parts: this.#partsHost(),
    };
  }

  /** The page's editor, built from open()'s start. */
  attach(editor: VisualEditor): void {
    this.#links.get("")!.editor = editor;
  }

  /** Something changed in the page's own text: a snapshot follows once typing pauses (and at least every 15 seconds). */
  changed(): void {
    this.#links.get("")?.changed();
  }

  /** Whether this browser has text the page doesn't have yet. */
  get unsaved(): boolean {
    return [...this.#links.values()].some((l) => l.unsaved);
  }

  /** Before leaving (Done, or Markdown mode): everything sent and saved, if it can be within a few seconds. */
  async flush(): Promise<boolean> {
    const until = Date.now() + 8000;
    while (Date.now() < until && [...this.#links.values()].some((l) => l.live?.sendable())) {
      for (const link of this.#links.values()) link.schedulePush();
      await sleep(150);
    }
    for (const link of this.#links.values()) await link.snapshot();
    while (Date.now() < until && this.unsaved) await sleep(150);
    return !this.unsaved;
  }

  /** Leaves the live text (still on the page, still editing it in Markdown mode, or on the way back to it). */
  async close(): Promise<void> {
    this.#closed = true;
    for (const link of this.#links.values()) link.close();
    for (const [name, handler] of this.#handlers) this.hub?.off(name, handler as never);
    this.#handlers = [];
    liveRows.set(null);
    try {
      await this.hub?.invoke("CloseDoc", this.page);
    } catch {
      // Gone already.
    }
  }

  restart(reason: string): void {
    if (this.#closed) return;
    this.#closed = true;
    const editor = this.#links.get("")?.editor;
    liveRows.set(null);
    this.host.restart(reason, editor?.live ? editor.getMarkdown() : null);
  }

  #on<T>(name: string, handler: (message: T) => void): void {
    this.hub.on(name, handler as never);
    this.#handlers.push([name, handler as never]);
  }

  async #reconnected(): Promise<void> {
    if (this.#closed) return;
    try {
      await this.hub.invoke("Join", this.page, true);
      const opened = (await this.hub.invoke("OpenDoc", this.page)) as Opened;
      // The server restarted, or the session ended meanwhile: start again from what it has.
      if (!opened.live || opened.sessionId !== this.#opened.sessionId) return this.restart("The live session started anew (the server restarted).");
      for (const link of this.#links.values()) {
        if (link.key !== "" && !(await this.hub.invoke("OpenPart", this.page, this.sessionId, link.key))) {
          this.#hide(link.key);
          continue;
        }
        await link.catchup();
        link.schedulePush();
      }
    } catch {
      this.restart("The live connection couldn't be picked up again.");
    }
  }

  // ---------------------------------------------------------------- restricted parts

  #partsHost(): PartsHost {
    return {
      mount: (key, host) => {
        const boxes = this.#boxes.get(key) ?? new Set<HTMLElement>();
        boxes.add(host);
        this.#boxes.set(key, boxes);
        void this.#draw(key, host);
        return () => {
          boxes.delete(host);
          const link = this.#links.get(key);
          if (link?.element?.parentElement === host) link.element.remove();
        };
      },
    };
  }

  // A restricted part's box: its editor (opened now, or moved here), or a locked box.
  async #draw(key: string, host: HTMLElement): Promise<void> {
    const part = this.#visible.get(key);
    if (!part || part.rows || this.#closed) {
      host.replaceChildren(locked());
      return;
    }
    let link = this.#links.get(key);
    if (!link) {
      link = new Link(this, key);
      this.#links.set(key, link);
      host.replaceChildren(note("Opening…"));
      const state = (await this.hub.invoke("OpenPart", this.page, this.sessionId, key).catch(() => null)) as PartState | null;
      if (!state || link.closed) {
        this.#links.delete(key);
        host.replaceChildren(locked());
        return;
      }
      const element = document.createElement("div");
      element.className = "pm-part-editor";
      element.append(part.to.trim() === NOT_REVEALED ? this.#unrevealedHead() : this.#partHead(key, part));
      const mount = document.createElement("div");
      element.append(mount);
      const start = this.#start(link, state);
      link.editor = this.host.partEditor(mount, state.markdown ?? "", start, () => link!.changed());
      link.element = element;
    }
    host.replaceChildren(link.element!);
  }

  // An unrevealed part's head (docs/tapestry-design.md §2.9): only the GMs see it. A box is revealed from the page (its
  // panel's Reveal) once nobody is writing in it live: taking a part out of the live text isn't done while live.
  #unrevealedHead(): HTMLElement {
    const head = document.createElement("div");
    head.className = "pm-part-head";
    const label = document.createElement("span");
    label.className = "pm-part-label";
    label.textContent = "Not revealed: only the GMs see this.";
    label.title = "To reveal it, use Reveal on its box on the page once you've finished editing";
    const hint = document.createElement("span");
    hint.className = "muted small";
    hint.textContent = " Reveal it from the page when you've finished editing.";
    head.append(label, hint);
    return head;
  }

  // A part's head: who it's for (changed here), and taking the part out of the page.
  #partHead(key: string, part: VisiblePart): HTMLElement {
    const head = document.createElement("div");
    head.className = "pm-part-head";
    const label = document.createElement("span");
    label.className = "pm-part-label";
    label.textContent = "Visible to:";
    const { element, input } = principalsInput({
      lookupUrl: this.host.lookupUrl, me: this.host.me, value: part.to, placeholder: "page owner and managers only",
      onChange: async (value) => {
        const problem = (await this.hub.invoke("SetAudience", this.page, this.sessionId, key, value).catch(() => "The live connection is gone.")) as string | null;
        if (problem) this.host.status(problem, true);
      },
    });
    input.title = "Only these @people and #groups (and the page's owner and managers) see this part";
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "small link danger";
    remove.textContent = "✕";
    remove.title = "Take this restricted part out of the page";
    remove.addEventListener("click", async () => {
      if (!window.confirm("Take this restricted part, and everything in it, out of the page?")) return;
      const problem = (await this.hub.invoke("DeletePart", this.page, this.sessionId, key).catch(() => "The live connection is gone.")) as string | null;
      if (problem) return this.host.status(problem, true);
      this.#hide(key);
      this.#removePlaceholder(key);
    });
    head.append(label, element, remove);
    return head;
  }

  // The placeholder of a part taken out, removed from the text it was in (on purpose: the guard lets it through).
  #removePlaceholder(key: string): void {
    for (const link of this.#links.values()) link.editor?.live?.removePart(key);
  }

  #hide(key: string): void {
    this.#visible.delete(key);
    const link = this.#links.get(key);
    if (link) {
      link.close();
      this.#links.delete(key);
    }
    for (const host of this.#boxes.get(key) ?? []) host.replaceChildren(locked());
    if (this.#rows.delete(key)) liveRows.changed();
  }

  async #refreshParts(): Promise<void> {
    const parts = (await this.hub.invoke("Parts", this.page).catch(() => null)) as VisiblePart[] | null;
    if (!parts) return;
    const now = new Map(parts.map((p) => [p.key, p]));
    for (const key of [...this.#visible.keys()]) if (!now.has(key)) this.#hide(key);
    for (const [key, part] of now) {
      const before = this.#visible.get(key);
      this.#visible.set(key, part);
      if (!before) for (const host of this.#boxes.get(key) ?? []) void this.#draw(key, host);
    }
    if (await this.#openRows()) liveRows.changed();
  }

  // The restricted rows this person may see, opened so the panels can show them.
  async #openRows(): Promise<boolean> {
    let opened = false;
    for (const part of this.#visible.values()) {
      if (!part.rows || this.#rows.has(part.key)) continue;
      const state = (await this.hub.invoke("OpenPart", this.page, this.sessionId, part.key).catch(() => null)) as PartState | null;
      if (!state) continue;
      this.#rows.set(part.key, state.markdown ?? "");
      opened = true;
    }
    return opened;
  }

  // The panels' view of restricted rows (live-rows.ts): visible ones expanded in place, written back to their parts.
  #rowsApi() {
    return {
      expand: (rows: string) => {
        const parts: RowsPart[] = [];
        const expanded = rows.replace(/^ {0,3}:{3,}[ \t]*hidden\b[^\n]*\bref="([^"]+)"[^\n]*\brows="yes"[^\n]*\n {0,3}:{3,}[ \t]*(?=\n|$)/gm, (whole, key: string) => {
          const text = this.#rows.get(key);
          const part = this.#visible.get(key);
          if (text === undefined || !part) return whole;
          parts.push({ key, to: part.to });
          const fence = ":".repeat(Math.max(3, ...text.split("\n").map((l) => (/^ {0,3}(:{3,})/.exec(l)?.[1]?.length ?? 2) + 1)));
          return `${fence}${part.to.trim() === NOT_REVEALED ? "unrevealed" : `restricted {to="${part.to}"}`}\n${text}\n${fence}`;
        });
        return { rows: expanded, parts };
      },
      collapse: (placed: Placed[], hidden: string[], parts: RowsPart[]) => {
        const shown: string[] = [];
        for (const part of parts) {
          // This part's rows, written as its own text (no restricted block around them: the part is that block).
          const mine = placed.filter((p) => p.to?.trim() === part.to.trim()).map((p) => ({ ...p, to: null }));
          if (mine.length === 0) {
            void this.hub.invoke("DeletePart", this.page, this.sessionId, part.key).catch(() => {});
            this.#hide(part.key);
            continue;
          }
          const text = writeRows(mine);
          if (text !== this.#rows.get(part.key)) {
            this.#rows.set(part.key, text);
            void this.hub.invoke("WriteRows", this.page, this.sessionId, part.key, text).then((problem) => problem && this.host.status(String(problem), true)).catch(() => {});
          }
          shown.push(`:::hidden {ref="${part.key}" rows="yes"}\n:::`);
        }
        const theirs = new Set(parts.map((p) => p.to.trim()));
        // Open rows, and rows for people no part is for yet (the server makes those a part of their own).
        const rest = placed.filter((p) => p.to === null || !theirs.has(p.to.trim()));
        return writeRows(rest, [...shown, ...hidden]);
      },
    };
  }
}

function locked(): HTMLElement {
  return note("Hidden block: content you can't see. It's kept as it is.");
}

function note(text: string): HTMLElement {
  const p = document.createElement("span");
  p.className = "pm-part-locked";
  p.textContent = text;
  return p;
}

function color(userId: string): string {
  let hash = 0;
  for (const ch of userId) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return COLORS[Math.abs(hash) % COLORS.length]!;
}

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
